// ===========================================
// Teacher Time-Off Warning Dialog
// ===========================================
// Scheduling into a teacher's APPROVED time off is a SOFT conflict: the admin may legitimately
// want it (the teacher agreed to cover this one, the time-off dates are about to be amended).
// So instead of the red blocking toast we show an AMBER warning naming the teacher and the real
// period dates, and let the admin proceed.
//
// [Schedule anyway] re-submits with `allowAwayOverride` — a NARROW server-side exception that
// forgives only the TEACHER_UNAVAILABLE range. A genuine double-booking (teacher already
// teaching, room taken) still hard-blocks with no escape hatch, and is never routed here.

import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  AlertTitle,
  Button,
  CircularProgress,
} from '@mui/material';

export interface TimeOffWarningDialogProps {
  open: boolean;
  /** Teacher's full name, when the server could resolve it. */
  teacherName?: string;
  /** The time-off reason, e.g. "Long service leave". */
  awayLabel?: string;
  /** Period bounds as `YYYY-MM-DD` (the WHOLE period, not the matched day). */
  awayFrom?: string;
  awayTo?: string;
  loading?: boolean;
  /** Proceed — re-submit with allowAwayOverride. */
  onScheduleAnyway: () => void;
  /** Back out and change the schedule instead. */
  onCancel: () => void;
}

/** `YYYY-MM-DD` → `D Mon YYYY`. Parsed as UTC so the date can't shift a day in a west-of-UTC zone. */
function formatDate(key?: string): string | undefined {
  if (!key) return undefined;
  const [y, m, d] = key.split('-').map(Number);
  if (!y || !m || !d) return undefined;
  return new Date(Date.UTC(y, m - 1, d)).toLocaleDateString(undefined, {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    timeZone: 'UTC',
  });
}

export default function TimeOffWarningDialog({
  open,
  teacherName,
  awayLabel,
  awayFrom,
  awayTo,
  loading = false,
  onScheduleAnyway,
  onCancel,
}: TimeOffWarningDialogProps) {
  const who = teacherName || 'This teacher';
  const from = formatDate(awayFrom);
  const to = formatDate(awayTo);
  // Single-day periods read better without a range.
  const range = from && to ? (from === to ? from : `${from} – ${to}`) : undefined;

  return (
    <Dialog open={open} onClose={loading ? undefined : onCancel} maxWidth="xs" fullWidth>
      <DialogTitle>Teacher is on approved time off</DialogTitle>
      <DialogContent>
        <Alert severity="warning">
          <AlertTitle>{range ? `${who} is away ${range}` : `${who} is on approved time off`}</AlertTitle>
          {awayLabel ? `Reason: ${awayLabel}. ` : ''}
          This class falls inside that period. You can schedule it anyway — the time off stays
          approved and unchanged — or pick a different day or time.
        </Alert>
      </DialogContent>
      <DialogActions>
        <Button onClick={onCancel} disabled={loading}>
          Change schedule
        </Button>
        <Button
          onClick={onScheduleAnyway}
          variant="contained"
          color="warning"
          disabled={loading}
          startIcon={loading ? <CircularProgress size={16} /> : undefined}
        >
          Schedule anyway
        </Button>
      </DialogActions>
    </Dialog>
  );
}
